import { supabase } from '../lib/supabase';

// Free "¿Te recomienda la IA?" check. The question is asked server-side by
// the `ai-visibility-check` Edge Function so the model API key never reaches
// the browser.
//
// Expected response from the function:
// {
//   success: true,
//   checkId: string,           // reference used to generate the paid report
//   question: string,          // the question a customer would ask
//   answer: string,            // the AI's answer, plain text
//   mentioned: boolean,        // did the answer name this business?
//   position: number | null,   // 1-based rank among recommendations
//   recommendations: [{ name: string }]
// }
export const aiVisibilityService = {
  async runCheck({ businessName, city, sector, website }) {
    if (!supabase) {
      return { success: false, message: 'La comprobación no está disponible ahora mismo. Inténtalo más tarde.' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('ai-visibility-check', {
        body: { businessName, city, sector, website }
      });

      if (error || !data?.success) {
        return {
          success: false,
          message: data?.message || 'No hemos podido hacer la consulta ahora mismo. Inténtalo de nuevo en unos minutos.'
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error('AI visibility check failed:', error);
      return {
        success: false,
        message: 'No hemos podido conectar con el servicio. Revisa tu conexión e inténtalo de nuevo.'
      };
    }
  },

  // Paid report unlocked with an invitation code instead of a card payment
  // (friends, tests). Returns the id of the report, which /informe/:id
  // shows as soon as it is ready.
  async redeemReport({ checkId, accessCode, website }) {
    if (!supabase) {
      return { success: false, message: 'El informe no está disponible ahora mismo. Inténtalo más tarde.' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('ai-report-generate', {
        body: { checkId, accessCode, website }
      });

      if (error || !data?.success || !data?.reportId) {
        return {
          success: false,
          message: data?.message || 'No hemos podido empezar el informe. Inténtalo de nuevo en unos minutos.'
        };
      }

      return { success: true, reportId: data.reportId };
    } catch (error) {
      console.error('Report redeem failed:', error);
      return {
        success: false,
        message: 'No hemos podido conectar con el servicio. Revisa tu conexión e inténtalo de nuevo.'
      };
    }
  },

  // "Avísame" for the monthly follow-up, from the end of a finished report.
  async joinMonitoringWaitlist({ reportId, email, priceCents }) {
    if (!supabase) return { success: false, message: 'No hemos podido apuntarte ahora mismo. Inténtalo más tarde.' };
    const { data, error } = await supabase.rpc('join_monitoring_waitlist', {
      p_report_id: reportId,
      p_email: email,
      p_price_cents: priceCents
    });
    if (error) return { success: false, message: 'No hemos podido apuntarte ahora mismo. Inténtalo más tarde.' };
    if (data === 'invalid_email') return { success: false, message: 'Revisa el email: parece que le falta algo.' };
    if (data !== 'ok') return { success: false, message: 'No hemos podido apuntarte ahora mismo. Inténtalo más tarde.' };
    return { success: true };
  },

  // Monthly follow-up with a free trial code (ai-monitoring Edge Function).
  // Returns { success, activeUntil, firstRunAt, emailSent } or a message.
  async activateMonitoring({ reportId, email, code }) {
    const fallback = 'No hemos podido activar el seguimiento. Inténtalo de nuevo.';
    if (!supabase) return { success: false, message: fallback };
    try {
      const { data, error } = await supabase.functions.invoke('ai-monitoring', {
        body: { action: 'activate', reportId, email, code }
      });
      if (error || !data) return { success: false, message: fallback };
      return data.success ? data : { success: false, message: data.message || fallback };
    } catch (error) {
      console.error('Monitoring activation failed:', error);
      return { success: false, message: 'No hemos podido conectar con el servicio. Revisa tu conexión e inténtalo de nuevo.' };
    }
  },

  // One-click unsubscribe from the link in the follow-up emails. Returns the
  // business name, or success:false for an unknown link.
  async unsubscribeMonitoring(token) {
    if (!supabase) return { success: false, message: 'El servicio no está disponible ahora mismo.' };
    const { data, error } = await supabase.rpc('unsubscribe_monitoring', { p_token: token });
    if (error || !data) {
      return {
        success: false,
        message:
          error && error.code !== '22P02'
            ? 'No hemos podido darte de baja ahora mismo. Inténtalo de nuevo en unos minutos.'
            : 'Este enlace de baja no es válido. Revisa que esté completo, tal como te llegó por email.'
      };
    }
    return { success: true, businessName: data };
  },

  // Card payment (ai-checkout Edge Function, Stripe Checkout). Returns the
  // url of Stripe's payment page for the business of this free check.
  startCheckout({ checkId, website }) {
    return checkout({ action: 'start', checkId, website }, 'No hemos podido abrir el pago. Inténtalo de nuevo.');
  },

  // When Stripe sends the customer back: the id of the report the payment
  // unlocked, or { success: false, status, message } (status: processing |
  // unpaid | exhausted | unknown | error).
  confirmPayment(sessionId) {
    return checkout({ action: 'confirm', sessionId }, 'No hemos podido confirmar el pago. Inténtalo de nuevo.');
  }
};

async function checkout(body, fallbackMessage) {
  if (!supabase) return { success: false, status: 'error', message: fallbackMessage };
  try {
    const { data, error } = await supabase.functions.invoke('ai-checkout', { body });
    if (error || !data) return { success: false, status: 'error', message: fallbackMessage };
    return data.success ? data : { ...data, status: data.status || 'error', message: data.message || fallbackMessage };
  } catch (error) {
    console.error('Checkout request failed:', error);
    return {
      success: false,
      status: 'error',
      message: 'No hemos podido conectar con el servicio. Revisa tu conexión e inténtalo de nuevo.'
    };
  }
}
